import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { pluralLabel } from 'LearningManager/helpers/role';

const Group = observer(() => {
	const { assignUsersStore } = useStores();
	const { groups, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);
	const { dropdownProps: propertyDropdownProps } = usePagedProperties(
		tenantId,
		isTenantResolved
	);
	const { t } = useTranslation();

	const {
		draft: {
			customSetup: {
				selectedGroups,
				selectedGroupPropertyIds,
				allGroupPropertiesSelected
			}
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');
	const hasPropertyScope =
		allGroupPropertiesSelected || selectedGroupPropertyIds.length > 0;

	useEffect(() => {
		if (allGroupPropertiesSelected && !isTenantResolved) return;
		assignUsersStore
			.fetchGroupsByProperty(selectedGroupPropertyIds, {
				allProperties: allGroupPropertiesSelected,
				tenantId
			})
			.catch(() => undefined);
	}, [
		selectedGroupPropertyIds,
		allGroupPropertiesSelected,
		isTenantResolved,
		tenantId
	]);

	const filtered = search.trim()
		? groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()))
		: groups;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const group = groups.find((g) => g.id === numId);
		if (!group) return;
		if (checked !== selectedGroups.some((g) => g.id === numId)) {
			toggleAssignUsersSelection('selectedGroups', {
				id: group.id,
				name: group.name,
				label: group.label
			});
		}
	};

	const handleSelectAll = () => {
		if (selectedGroups.length === filtered.length) {
			setAssignUsersSelection('selectedGroups', []);
		} else {
			setAssignUsersSelection(
				'selectedGroups',
				filtered.map((g) => ({
					id: g.id,
					name: g.name,
					label: g.label
				}))
			);
		}
	};

	const isAllSelected =
		filtered.length > 0 &&
		filtered.every((g) => selectedGroups.some((sg) => sg.id === g.id));
	const isLoading = status.fetchGroups === Status.Pending;

	return (
		<div id="lms-ca-assign-users-group">
		<div>
			<Heading />
			<div className={styles.filters}>
				<div
					id="lms-ca-au-group-property-filter"
					className={styles.search}
				>
					<div className={styles.selectProp}>
						{t('learning.assignUsers.options.group.selectProperty')}
					</div>
					<MultiSelectDropdown
						{...propertyDropdownProps}
						searchable
						searchPlaceholder={t(
							'learning.assignUsers.options.group.searchProperties'
						)}
						placeholder={t('learning.assignUsers.options.group.selectOption')}
						onChange={(ids, allSelected) => {
							setAssignUsersPropertyFilter(
								'selectedGroupPropertyIds',
								ids,
								allSelected
							);
						}}
						defaultSelectedIds={selectedGroupPropertyIds}
						defaultAllSelected={allGroupPropertiesSelected}
					/>
				</div>
				<Input.Search
					id="lms-ca-au-group-search"
					className={styles.search}
					placeholder={t('learning.assignUsers.options.group.search')}
					suffix={<Icon type="search" role="img" />}
					// Keeps a suffix rendered, so the input isn't remounted mid-typing.
					allowClear
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
		</div>
		<div className={styles.header}>
			<Heading
				titleClassName={styles.title}
				title={t('learning.assignUsers.options.group.selectGroups')}
				astrix={true}
			/>
			{filtered.length > 1 && (
				<div
					id="lms-ca-au-group-select-all"
					className={styles.selectAllBtn}
					onClick={handleSelectAll}
				>
					{isAllSelected
						? t('learning.assignUsers.options.group.deselectAll')
						: t('learning.assignUsers.options.group.selectAll')}
				</div>
			)}
		</div>
		<div id="lms-ca-au-group-list" className={styles.container}>
			{isLoading && (
				<div className={styles.loading}>
					<Spin />
				</div>
			)}
			{!isLoading && !hasPropertyScope && (
				<div className={styles.empty}>
					{t('learning.assignUsers.options.group.selectPropertyToSeeGroups')}
				</div>
			)}
			{!isLoading && hasPropertyScope && filtered.length === 0 && (
				<div className={styles.empty}>
					{t('learning.assignUsers.options.group.noGroupsFound')}
				</div>
			)}
			{filtered.map((group) => {
				const isChecked = selectedGroups.some(
					(g) => g.id === group.id
				);
				return (
					<CheckBoxCard
						key={group.id}
						id={group.id}
						htmlId={`lms-ca-au-group-${group.id}`}
						icon={
							<Icon
								type={
									isChecked
										? 'groupOptFilled'
										: 'groupOptIcon'
								}
							/>
						}
						title={group.name}
						desc={
							group.userCount === undefined
								? ''
								: t(pluralLabel('learning.courseAssignments.bulkResetModal.users', group.userCount), { count: group.userCount })
						}
						isChecked={isChecked}
						onChange={handleCheckboxChange}
					/>
				);
			})}
		</div>
	</div>
	);
});

export default Group;
